// Hook for managing company-level Project Custom Field definitions.
// Used by:
//   - Settings → Project Settings → Custom Fields tab (CRUD operations)
//   - Project Overview tab (read definitions to render the Project Attributes card)

import { useState, useCallback, useEffect } from 'react';
import {
  fetchCustomFieldDefinitions,
  saveCustomFieldDefinitionsBulk,
  getDataMode,
} from '@/lib/data-service';
import type { CustomFieldDefinitionResponse } from '@/lib/data-service';
import type { ProjectCustomFieldDefinition } from '@/types';

export const MAX_FIELDS = 20;

export interface UseProjectCustomFieldsReturn {
  definitions: ProjectCustomFieldDefinition[];
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  addDefinition: (def: Omit<ProjectCustomFieldDefinition, 'id' | 'displayOrder'>) => void;
  updateDefinition: (id: string, patch: Partial<ProjectCustomFieldDefinition>) => void;
  removeDefinition: (id: string) => void;
  reorderDefinitions: (orderedIds: string[]) => void;
  saveDefinitions: () => Promise<void>;
  discardChanges: () => void;
  reload: () => Promise<void>;
}

function generateId(): string {
  // Generate a valid RFC-4122 v4 GUID so the backend can deserialize it as System.Guid.
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx  (y ∈ {8,9,a,b})
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Map a backend response definition to the frontend type. */
function mapResponseToDefinition(resp: CustomFieldDefinitionResponse): ProjectCustomFieldDefinition {
  let parsedOptions: string[] | undefined;
  if (resp.options) {
    try {
      const parsed = JSON.parse(resp.options);
      if (Array.isArray(parsed)) {
        parsedOptions = parsed;
      }
    } catch {
      // ignore malformed JSON
    }
  }

  return {
    id: resp.id,
    label: resp.label,
    fieldType: resp.fieldType as ProjectCustomFieldDefinition['fieldType'],
    required: resp.required,
    enabled: resp.enabled,
    displayOrder: resp.displayOrder,
    options: parsedOptions,
    placeholder: resp.placeholder ?? undefined,
  };
}

export function useProjectCustomFields(): UseProjectCustomFieldsReturn {
  const [definitions, setDefinitions] = useState<ProjectCustomFieldDefinition[]>([]);
  const [savedDefinitions, setSavedDefinitions] = useState<ProjectCustomFieldDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges =
    JSON.stringify(definitions) !== JSON.stringify(savedDefinitions);

  // Load definitions from API on mount
  const loadDefinitions = useCallback(async () => {
    setIsLoading(true);
    try {
      const mode = getDataMode();
      if (mode === 'demo' || mode === 'tour') {
        // In demo/tour mode, keep using localStorage as fallback
        const { getProjectCustomFieldDefinitions } = await import('@/lib/project-custom-fields-storage');
        const defs = getProjectCustomFieldDefinitions();
        setDefinitions(defs);
        setSavedDefinitions(defs);
      } else {
        const response = await fetchCustomFieldDefinitions();
        const mapped = response.map(mapResponseToDefinition);
        setDefinitions(mapped);
        setSavedDefinitions(mapped);
      }
    } catch (err) {
      console.error('Failed to load custom field definitions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const addDefinition = useCallback(
    (def: Omit<ProjectCustomFieldDefinition, 'id' | 'displayOrder'>) => {
      setDefinitions((prev) => {
        if (prev.length >= MAX_FIELDS) return prev;
        const next: ProjectCustomFieldDefinition = {
          ...def,
          id: generateId(),
          displayOrder: prev.length,
        };
        return [...prev, next];
      });
    },
    []
  );

  const updateDefinition = useCallback(
    (id: string, patch: Partial<ProjectCustomFieldDefinition>) => {
      setDefinitions((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...patch } : d))
      );
    },
    []
  );

  const removeDefinition = useCallback((id: string) => {
    setDefinitions((prev) => {
      const filtered = prev.filter((d) => d.id !== id);
      // Re-number displayOrder after removal
      return filtered.map((d, i) => ({ ...d, displayOrder: i }));
    });
  }, []);

  const reorderDefinitions = useCallback((orderedIds: string[]) => {
    setDefinitions((prev) => {
      const byId = new Map(prev.map((d) => [d.id, d]));
      return orderedIds
        .filter((id) => byId.has(id))
        .map((id, i) => ({ ...byId.get(id)!, displayOrder: i }));
    });
  }, []);

  const saveDefinitions = useCallback(async () => {
    setIsSaving(true);
    try {
      const mode = getDataMode();
      if (mode === 'demo' || mode === 'tour') {
        // In demo/tour mode, persist to localStorage
        const { saveProjectCustomFieldDefinitions } = await import('@/lib/project-custom-fields-storage');
        await new Promise((resolve) => setTimeout(resolve, 400));
        saveProjectCustomFieldDefinitions(definitions);
        setSavedDefinitions(definitions);
      } else {
        // Live mode — bulk save to backend
        const bulkItems = definitions.map((d) => ({
          id: d.id,
          label: d.label,
          fieldType: d.fieldType,
          required: d.required,
          enabled: d.enabled,
          options: d.options ?? null,
          placeholder: d.placeholder ?? null,
        }));
        const response = await saveCustomFieldDefinitionsBulk(bulkItems);
        const mapped = response.map(mapResponseToDefinition);
        setDefinitions(mapped);
        setSavedDefinitions(mapped);
      }
    } finally {
      setIsSaving(false);
    }
  }, [definitions]);

  const discardChanges = useCallback(() => {
    setDefinitions(savedDefinitions);
  }, [savedDefinitions]);

  return {
    definitions,
    isLoading,
    hasUnsavedChanges,
    isSaving,
    addDefinition,
    updateDefinition,
    removeDefinition,
    reorderDefinitions,
    saveDefinitions,
    discardChanges,
    reload: loadDefinitions,
  };
}
